/**
 *
 * @param {Number} length
 * @param {Boolean} numbers
 * @param {Boolean} symbols
 * @param {Boolean} uppercase
 * @param {Boolean} lowercase
 * @param {Boolean} excludeSimilarCharacters
 * @returns String
 * @description Generate a random password
 * @example GeneratePassword({ length: 15, numbers: true, symbols: true, uppercase: true, lowercase: true, excludeSimilarCharacters: true })
 *
 */
export const GeneratePassword = ({
  length = 15,
  numbers = true,
  symbols = true,
  uppercase = true,
  lowercase = true,
  excludeSimilarCharacters = true,
} = {}) => {
  const similarChars = ['l', '1', 'I', 'O'];

  let characterPool = '';

  if (lowercase) {
    let lowercaseChars = '';
    for (let i = 97; i <= 122; i++) {
      lowercaseChars += String.fromCharCode(i);
    }
    if (excludeSimilarCharacters) {
      lowercaseChars = lowercaseChars
        .split('')
        .filter((char) => !similarChars.includes(char))
        .join('');
    }
    characterPool += lowercaseChars;
  }

  if (uppercase) {
    let uppercaseChars = '';
    for (let i = 65; i <= 90; i++) {
      uppercaseChars += String.fromCharCode(i);
    }
    if (excludeSimilarCharacters) {
      uppercaseChars = uppercaseChars
        .split('')
        .filter((char) => !similarChars.includes(char))
        .join('');
    }
    characterPool += uppercaseChars;
  }

  if (numbers) {
    let numericChars = '';
    for (let i = 48; i <= 57; i++) {
      numericChars += String.fromCharCode(i);
    }
    if (excludeSimilarCharacters) {
      numericChars = numericChars
        .split('')
        .filter((char) => !similarChars.includes(char))
        .join('');
    }
    characterPool += numericChars;
  }

  if (symbols) {
    characterPool += '!@#$%^&*()-=_+[]{}|;:,.<>?';
  }

  let password = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characterPool.length);
    password += characterPool[randomIndex];
  }

  return password;
};

/**
 * Global function to automatically trim leading and trailing spaces from input fields
 * @description Sets up event listeners to trim spaces from input fields on form submit
 */
export const initializeInputTrimming = () => {
  // Function to trim input value (only removes leading/trailing spaces)
  const trimInputValue = (input) => {
    const trimmedValue = input.value.trim();
    if (input.value !== trimmedValue) {
      input.value = trimmedValue;
      // Trigger input event to notify Vue/framework of the change
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  // Function to trim all form inputs
  const trimFormInputs = (form) => {
    const inputs = form.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="search"], input[type="url"], input[type="tel"], input:not([type]), textarea'
    );
    inputs.forEach(trimInputValue);
  };

  // Event listener for form submissions
  const handleFormSubmit = (event) => {
    const form = event.target;
    if (form.tagName === 'FORM') {
      trimFormInputs(form);
    }
  };

  // Event listener for individual button clicks (for cases where forms don't use standard submit)
  const handleButtonClick = (event) => {
    const button = event.target;
    if (
      button.tagName === 'BUTTON' &&
      (button.type === 'submit' || !button.type)
    ) {
      // Find the parent form
      const form = button.closest('form');
      if (form) {
        trimFormInputs(form);
      } else {
        // If no form found, trim all visible inputs on the page
        const allInputs = document.querySelectorAll(
          'input[type="text"], input[type="email"], input[type="search"], input[type="url"], input[type="tel"], input:not([type]), textarea'
        );
        allInputs.forEach(trimInputValue);
      }
    }
  };

  // Event listener for input elements with Enter key (common submit pattern)
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const target = event.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Find the parent form
        const form = target.closest('form');
        if (form) {
          // Check if this Enter keypress would submit the form
          const submitButtons = form.querySelectorAll(
            'button[type="submit"], input[type="submit"]'
          );
          if (
            submitButtons.length === 0 ||
            (submitButtons.length === 1 && !event.shiftKey)
          ) {
            trimFormInputs(form);
          }
        }
      }
    }
  };

  // Add event listeners using event delegation for better performance
  document.addEventListener('submit', handleFormSubmit, true);
  document.addEventListener('click', handleButtonClick, true);
  document.addEventListener('keydown', handleKeyDown, true);

  // Observer to handle dynamically added forms and inputs
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // The global event listeners will handle newly added forms and inputs automatically
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Return cleanup function
  return () => {
    document.removeEventListener('submit', handleFormSubmit, true);
    document.removeEventListener('click', handleButtonClick, true);
    document.removeEventListener('keydown', handleKeyDown, true);
    observer.disconnect();
  };
};
